import React from "react";
import { ReactElement } from "react";
import { DetailsList, DetailsListLayoutMode, IColumn, IDetailsRowProps, mergeStyleSets, useTheme } from "@fluentui/react";
import { SelectionMode } from "@fluentui/utilities";
import { IRcaMtaStsValidationStatus } from "../../models/IRcaMtaStsValidationStatus";
import { MtaStsValidationResultSectionIcon } from "./MtaStsValidationResultIcon";

/**
 * The table row items structure
 */
export interface IMtaStsValidationTableItem {
  /**
   * Component to be shown. It includes the keyword of the row.
   */
  Component: string;

  /**
   * The value of the retrieved content for the row.
   */
  Value: string | number;

  /**
   * Status of the content. It can have values between Success, Invalid Confgiuration or Failed.
   */
  Status: IRcaMtaStsValidationStatus | number;

  /**
   * Record details \ error message
   */
  Details: string | ReactElement;
}

const classNames = mergeStyleSets({
  wrapper: {
    marginBottom: 16,
    marginTop: 10,
  },
});

/**
 * @returns result table columns
 */
const getResultTableColumns = (): IColumn[] => {
  return [
    {
      key: "recordType",
      name: WebsiteStrings.MtaStsComponent,
      fieldName: "Component",
      minWidth: 130,
      maxWidth: 180,
      isResizable: true,
      isPadded: true,
    },
    {
      key: "value",
      name: WebsiteStrings.MtaStsValue,
      fieldName: "Value",
      minWidth: 130,
      maxWidth: 300,
      isResizable: true,
      isPadded: true,
      isMultiline: true,
      styles: {
        root: {
          whiteSpace: "pre-line !important",
        },
      },
    },
    {
      key: "status",
      name: WebsiteStrings.MtaStsStatus,
      minWidth: 50,
      maxWidth: 50,
      isResizable: true,
      isPadded: true,
      onRender: (item: IMtaStsValidationTableItem) => {
        return <MtaStsValidationResultSectionIcon mtaStsStatus={item.Status} />;
      },
    },
    {
      key: "details",
      name: WebsiteStrings.MtaStsDetails,
      fieldName: "Details",
      minWidth: 100,
      maxWidth: 300,
      isResizable: true,
      isPadded: true,
      isMultiline: true,
    },
  ];
};

/**
 * @param props The component properties
 * @returns The markup
 */
const MtaStsValidationResultTable: React.FC<{
  /**
   * List of items to render in the table
   */
  items: IMtaStsValidationTableItem[];
}> = ({ items }) => {
  const columns = getResultTableColumns();
  const theme = useTheme();

  return (
    <DetailsList
      className={classNames.wrapper}
      styles={{
        root: {
          backgroundColor: `var(--rca-control-background, ${theme.semanticColors.bodyBackground})`,
          color: theme.semanticColors.bodyText,
        },
        headerWrapper: {
          backgroundColor: `var(--rca-control-background, ${theme.semanticColors.bodyBackground})`,
        },
        contentWrapper: {
          backgroundColor: `var(--rca-control-background, ${theme.semanticColors.bodyBackground})`,
        },
      }}
      items={items}
      compact={false}
      columns={columns}
      selectionMode={SelectionMode.none}
      getKey={(_, i: number) => i.toString()}
      setKey="none"
      layoutMode={DetailsListLayoutMode.justified}
      isHeaderVisible={true}
      onRenderRow={(props: IDetailsRowProps, defaultRender) => {
        return defaultRender
          ? defaultRender({
              ...props,
              styles: {
                root: {
                  backgroundColor: `var(--rca-control-background, ${theme.semanticColors.bodyBackground})`,
                  color: theme.semanticColors.bodyText,
                },
                rootHovered: {
                  backgroundColor: theme.semanticColors.bodyBackgroundHovered,
                  color: theme.semanticColors.bodyText,
                },
                cell: {
                  color: theme.semanticColors.bodyText,
                  whiteSpace: "pre-line",
                },
                isMultiline: {
                  whiteSpace: "pre-line",
                },
              },
            })
          : null;
      }}
    />
  );
};

export default MtaStsValidationResultTable;
